import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const INTEREST_OPTIONS = [
  { key: "waldorf", label: "Waldorf pedagojisi" },
  { key: "doga_egitimi", label: "Doğa eğitimi" },
  { key: "ev_okulu", label: "Ev okulu" },
  { key: "el_isi", label: "El işi / keçe / ahşap" },
  { key: "mevsimsel_ritim", label: "Mevsimsel ritim" },
  { key: "ogretmen", label: "Öğretmenim" },
];

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: "", city: "", bio: "", interests: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        city: user.city || "",
        bio: user.bio || "",
        interests: user.interests || [],
      });
    }
  }, [user]);

  const toggleInterest = (key) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(key) ? f.interests.filter((k) => k !== key) : [...f.interests, key],
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.patch("/auth/me", form);
      setUser(data);
      toast.success("Profil güncellendi");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Güncelleme başarısız");
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <div className="k-container py-12 md:py-16">
      <div className="mx-auto max-w-2xl">
        <div className="flex items-center gap-5">
          <div className="grid h-20 w-20 place-items-center rounded-full bg-sage-500 text-3xl font-bold text-cream-50">
            {user.name?.[0]?.toUpperCase() || "K"}
          </div>
          <div>
            <h1 className="font-display text-5xl text-ink-900" data-testid="profile-name">
              {user.name}
            </h1>
            <p className="font-body text-ink-500">
              {user.email} · {user.role === "admin" ? "Yönetici" : "Üye"}
            </p>
          </div>
        </div>

        <div className="k-card mt-10 p-8">
          <form onSubmit={save} className="space-y-5" data-testid="profile-form">
            <div>
              <label className="k-label">İsim</label>
              <input
                className="k-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="profile-name-input"
              />
            </div>
            <div>
              <label className="k-label">Şehir</label>
              <input
                className="k-input"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                data-testid="profile-city-input"
              />
            </div>
            <div>
              <label className="k-label">Kısa tanıtım</label>
              <textarea
                rows={4}
                className="k-input resize-none"
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                placeholder="Kendinden birkaç cümleyle bahset…"
                data-testid="profile-bio-input"
              />
            </div>
            <div>
              <label className="k-label">İlgi alanların</label>
              <div className="flex flex-wrap gap-2">
                {INTEREST_OPTIONS.map((o) => {
                  const active = form.interests.includes(o.key);
                  return (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => toggleInterest(o.key)}
                      data-testid={`profile-interest-${o.key}`}
                      className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
                        active
                          ? "border-sage-500 bg-sage-500 text-cream-50"
                          : "border-cream-300 bg-cream-50 text-ink-700 hover:border-sage-500/60"
                      }`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {error && (
              <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="k-btn-primary disabled:opacity-60"
              data-testid="profile-save-btn"
            >
              {busy ? "Kaydediliyor…" : "Değişiklikleri kaydet"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
